import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../hooks/useClickOutside';
import { ChevronDown } from './icons/ChevronDown';

const CONTROL_BASE =
  'flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-sm border bg-surface-raised px-3 text-[0.9rem] text-fg transition-colors';

export default function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchable = false,
  label,
  className,
  controlClassName = '',
  menuClassName = '',
  renderSelected,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const controlRef = useRef(null);
  const listboxId = useId();

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setActiveIndex(-1);
  }, []);

  useClickOutside(containerRef, close, isOpen);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter((option) => String(option.label).toLowerCase().includes(q));
  }, [options, searchable, searchQuery]);

  const selectedOption = options.find((opt) => opt.value === value);

  const open = useCallback(() => {
    const index = filteredOptions.findIndex((opt) => opt.value === value);
    setActiveIndex(index >= 0 ? index : 0);
    setIsOpen(true);
  }, [filteredOptions, value]);

  useEffect(() => {
    if (isOpen && searchable) searchInputRef.current?.focus();
  }, [isOpen, searchable]);

  // Keep the highlighted option in view while arrowing through a long list
  // (the timezone picker has ~430 entries).
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, activeIndex]);

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    close();
    controlRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        open();
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        controlRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(filteredOptions.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredOptions[activeIndex]) handleSelect(filteredOptions[activeIndex].value);
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`relative w-full ${className ?? ''}`}
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      {label && (
        <label
          htmlFor={`${listboxId}-control`}
          className="mb-1 block text-[0.8rem] font-medium text-fg-muted"
        >
          {label}
        </label>
      )}

      <div
        id={`${listboxId}-control`}
        ref={controlRef}
        role="combobox"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-label={label ?? placeholder}
        className={`${CONTROL_BASE} ${isOpen ? 'border-accent' : 'border-line'} ${controlClassName}`}
        onClick={() => (isOpen ? close() : open())}
      >
        <span className={`truncate ${selectedOption ? 'text-fg' : 'text-fg-muted'}`}>
          {renderSelected ? renderSelected(selectedOption) : (selectedOption?.label ?? placeholder)}
        </span>
        <ChevronDown
          className={`shrink-0 text-fg-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {isOpen && (
        <div
          className={`absolute top-full right-0 left-0 z-[1000] mt-1.5 max-h-[250px] overflow-x-hidden overflow-y-auto rounded-md border border-line bg-surface-elevated shadow-medium ${menuClassName}`}
        >
          {searchable && (
            <input
              ref={searchInputRef}
              type="text"
              aria-label="Filter options"
              className="w-full border-b border-line bg-transparent px-3 py-2 text-[0.9rem] text-fg outline-none"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setActiveIndex(0);
              }}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {filteredOptions.length > 0 ? (
            <ul id={listboxId} ref={listRef} role="listbox" aria-label={label ?? placeholder}>
              {filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex cursor-pointer items-center justify-between border-l-[3px] py-2.5 pr-3 pl-[9px] text-[0.95rem] leading-tight transition-colors ${
                      isSelected
                        ? 'border-l-accent bg-accent-soft font-medium text-accent'
                        : 'border-l-transparent text-fg'
                    } ${isActive && !isSelected ? 'bg-surface-raised' : ''} ${
                      isActive && isSelected ? 'bg-accent-strong' : ''
                    }`}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    {option.label}
                    {isSelected && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-3 py-2.5 text-center text-[0.9rem] text-fg-muted">
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
